import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64Url(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function usePushNotifications() {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [permission, setPermission] = useState('default');
  const autoSubAttempted = useRef(false);

  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
    setIsSupported(supported);
    if (supported) {
      setPermission(Notification.permission);
      // Force update the service worker to get latest push handler
      navigator.serviceWorker.getRegistration('/').then(reg => {
        if (reg) reg.update().catch(() => {});
      });
      checkAndFixSubscription();
    }
  }, []);

  const checkAndFixSubscription = async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (!reg) {
        setIsSubscribed(false);
        return;
      }
      const sub = await reg.pushManager.getSubscription();
      if (!sub) {
        setIsSubscribed(false);
        return;
      }

      // Check if subscription's VAPID key matches the server's current key
      try {
        const { data } = await axios.get(`${API}/push/vapid-key`);
        const serverKey = data.public_key;
        const subKey = sub.options?.applicationServerKey
          ? arrayBufferToBase64Url(sub.options.applicationServerKey)
          : null;

        if (subKey && serverKey && subKey !== serverKey) {
          console.log('Push: VAPID key mismatch, re-subscribing...');
          await sub.unsubscribe();
          setIsSubscribed(false);
          // Auto re-subscribe if permission was already granted
          if (Notification.permission === 'granted') {
            const token = localStorage.getItem('rollm8s_token');
            if (token) {
              await doSubscribe(reg, serverKey, token);
            }
          }
          return;
        }
      } catch (e) {
        console.debug('Push: VAPID check failed', e);
      }

      // Subscription looks valid — make sure backend knows about it
      setIsSubscribed(true);
      const token = localStorage.getItem('rollm8s_token');
      if (token) {
        try {
          await axios.post(`${API}/push/subscribe`, {
            subscription: sub.toJSON(),
          }, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } catch {}
      }
    } catch {
      setIsSubscribed(false);
    }
  };

  const doSubscribe = async (reg, serverPublicKey, token) => {
    try {
      const applicationServerKey = urlBase64ToUint8Array(serverPublicKey);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
      await axios.post(`${API}/push/subscribe`, {
        subscription: subscription.toJSON(),
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsSubscribed(true);
      return true;
    } catch (err) {
      console.error('Push auto-subscribe error:', err);
      return false;
    }
  };

  // Auto-subscribe when user is logged in and permission is granted
  useEffect(() => {
    if (!isSupported || isSubscribed || autoSubAttempted.current) return;
    if (Notification.permission !== 'granted') return;
    const token = localStorage.getItem('rollm8s_token');
    if (!token) return;

    autoSubAttempted.current = true;
    (async () => {
      try {
        let reg = await navigator.serviceWorker.getRegistration('/sw.js');
        if (!reg) {
          reg = await navigator.serviceWorker.register('/sw.js');
          await navigator.serviceWorker.ready;
        }
        const { data } = await axios.get(`${API}/push/vapid-key`);
        await doSubscribe(reg, data.public_key, token);
      } catch (e) {
        console.debug('Push: auto-subscribe failed', e);
      }
    })();
  }, [isSupported, isSubscribed]);

  const subscribe = useCallback(async () => {
    if (!isSupported) {
      console.warn('Push: not supported in this browser');
      return false;
    }
    try {
      console.log('Push: requesting permission...');
      const perm = await Notification.requestPermission();
      console.log('Push: permission result:', perm);
      setPermission(perm);
      if (perm !== 'granted') return false;

      console.log('Push: registering service worker...');
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      console.log('Push: service worker ready');

      const { data } = await axios.get(`${API}/push/vapid-key`);
      console.log('Push: got VAPID key');
      const token = localStorage.getItem('rollm8s_token');
      return await doSubscribe(reg, data.public_key, token);
    } catch (err) {
      console.error('Push subscribe error:', err);
      return false;
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.getRegistration('/sw.js');
      if (reg) {
        const sub = await reg.pushManager.getSubscription();
        if (sub) await sub.unsubscribe();
      }
      const token = localStorage.getItem('rollm8s_token');
      await axios.post(`${API}/push/unsubscribe`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setIsSubscribed(false);
      return true;
    } catch (err) {
      console.error('Push unsubscribe error:', err);
      return false;
    }
  }, []);

  return { isSupported, isSubscribed, permission, subscribe, unsubscribe };
}

export default usePushNotifications;
